'use client';

import FilterContainer from '@/components/filters/FilterContainer';
import type { Filter, MeasurementUnit } from '@/models/Filter';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SearchBarContainer from '@/components/search-bar/SearchBarContainer';
import type { FilterOptionsAndLabels } from '@/utils/filters/options/getFilterOptions';
import type { Locale } from '@/i18n';
import calcHeaderHeight, { BASE_HEADER_HEIGHT } from '@/utils/calcHeaderHeight';
import type { QueryParserResult } from '@/fetchers/getQueryParserResult';
import useMap from '@/hooks/useMap';
import SortBy from '@/components/SortBy';
import { CSN_SEARCH_BANNER_COOKIE_NAME } from '@/constants/cookieConstants';
import type { FeatureConfig } from '@/fetchers/getFeatureConfigs';
import { formatDate } from '@/utils/translationHelper';
import SearchToggle from '@/components/ai/SearchToggle';
import { MAX_ATTENDEES_LIMIT, MAX_GUEST_ROOMS_LIMIT } from '@/constants/filterConstants';
import { calculateRoomSpaceValue } from '@/utils/filters/spaceCalculator';
import { DateRange } from 'react-day-picker';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import { useApplyFilter } from '@/hooks/useApplyFilter';
import { useAppFeatures } from '@/hooks/useAppFeatures';
import AISearchContainer from '@/components/ai/AISearchContainer';
import { useSearchParams } from 'next/navigation';
import { VIEW, queryStringConstants } from '@/constants/queryConstants';
import CollapsedSearch from '@/components/search-container/CollapsedSearch';
import ExpandedSearchContainer from '@/components/search-container/ExpandedSearchContainer';

interface Props {
  searchTerm: string;
  geo: string | undefined;
  sortOptions: { label: string; value: string }[];
  sortType: string;
  sortLabel: string;
  applicableFilters: Array<string>;
  appliedFilters: Filter;
  countryCode: string | undefined;
  isNotificationBannerVisible: boolean;
  isLoggedIn: boolean;
  options: FilterOptionsAndLabels;
  queryParserResult: QueryParserResult;
  radius: number | undefined;
  locale: Locale;
  measurementUnit: MeasurementUnit;
  locationRadiusMessage: string;
  cookieDomain: string;
  rfpId: string | undefined;
  featureConfig: FeatureConfig | null;
  basePath: string;
  isAIEnabledForAccount: boolean;
}

function getStyles(
  isNotificationBannerVisible: boolean,
  isLoggedIn: boolean,
  isAllPanelOpen: boolean,
  isModalOpen: boolean,
  expanded: boolean
): string {
  const headerHeight = calcHeaderHeight(BASE_HEADER_HEIGHT, isNotificationBannerVisible, isLoggedIn);
  const mobileHeaderHeight = calcHeaderHeight(BASE_HEADER_HEIGHT, isNotificationBannerVisible, isLoggedIn, true);
  const zIndex = isAllPanelOpen ? 'z-50' : 'z-20';
  const blur = isModalOpen ? 'blur-xs opacity-80' : '';

  // possible values of top-[3.75rem], top-[5rem], top-[6.5rem], top-[7.75rem], top-[8rem], top-[9.25rem], xl:top-[3.75rem], xl:top-[5rem], xl:top-[6.5rem], xl:top-[7.75rem]
  return `sticky w-full left-0 shadow-2 h-[5.75rem] hover:z-1 bg-background flex items-center lg:items-start flex-row lg:flex-col gap-4 top-[${mobileHeaderHeight}rem] xl:top-[${headerHeight}rem] ${zIndex} ${blur} ${
    !expanded ? 'p-2 px-4' : 'p-4'
  }`;
}

export default function FiltersAndSearch({
  searchTerm,
  geo,
  sortOptions,
  sortType,
  sortLabel,
  applicableFilters,
  appliedFilters,
  countryCode,
  isNotificationBannerVisible,
  isLoggedIn,
  options,
  queryParserResult,
  radius,
  locale,
  measurementUnit,
  locationRadiusMessage,
  cookieDomain,
  rfpId,
  featureConfig,
  basePath,
  isAIEnabledForAccount
}: Props): JSX.Element {
  const [isSearchExpanded, setSearchExpanded] = useState(false);
  const { aiSearch, aiSearchLoggedIn } = useAppFeatures();
  const isAISearchEnabled = (isLoggedIn && aiSearchLoggedIn && isAIEnabledForAccount) || aiSearch;
  const searchParams = useSearchParams();
  const [aiMode, setAIMode] = useState(isAISearchEnabled && searchParams.get(queryStringConstants.AI) === 'true');
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [sortingType, setSortingType] = useState(sortType);
  const [sortingLabel, setSortingLabel] = useState(sortLabel);
  const [sort, setSort] = useState<string>(sortingType);
  const [isRfpBannerVisible, setIsRfpBannerVisible] = useState<boolean>(true);
  const [searchBarFilters, setSearchBarFilters] = useState(appliedFilters);
  const { venueRatesModal } = useMap();
  const { applyFilters } = useApplyFilter();
  const view = useMemo(() => searchParams?.get(queryStringConstants.VIEW), [searchParams]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isGuestRoomDatePickerOpen, setIsGuestRoomDatePickerOpen] = useState(false);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);

  // input refs
  const inputRefs = {
    searchTermInputRef: useRef<HTMLInputElement>(null),
    startInputRef: useRef<HTMLDivElement>(null),
    endInputRef: useRef<HTMLDivElement>(null),
    attendeesInputRef: useRef<HTMLInputElement>(null),
    guestRoomsInputRef: useRef<HTMLInputElement>(null)
  };

  const { isMobile, isTablet } = useBreakpoints();
  const isMobileOrTablet = isMobile || isTablet;

  // formatted dates to be shown in input box
  const [eventStart, setEventStart] = useState<string | undefined>(
    appliedFilters.EVENT_START_DATE
      ? formatDate(locale, appliedFilters.EVENT_START_DATE, { timeZone: appliedFilters?.TIMEZONE })
      : ''
  );
  const [eventEnd, setEventEnd] = useState<string | undefined>(
    appliedFilters.EVENT_END_DATE
      ? formatDate(locale, appliedFilters.EVENT_END_DATE, { timeZone: appliedFilters?.TIMEZONE })
      : ''
  );

  const [checkInDate, setCheckInDate] = useState<Date | undefined>(
    appliedFilters.GUEST_CHECKIN_DATE ?? appliedFilters.EVENT_START_DATE
  );
  const [checkOutDate, setCheckOutDate] = useState<Date | undefined>(
    appliedFilters.GUEST_CHECKOUT_DATE ?? appliedFilters.EVENT_END_DATE
  );

  const styles = useMemo(
    () => getStyles(isNotificationBannerVisible, isLoggedIn, isFiltersPanelOpen, venueRatesModal, isSearchExpanded),
    [isNotificationBannerVisible, isLoggedIn, isFiltersPanelOpen, venueRatesModal, isSearchExpanded]
  );

  if (isLoggedIn && rfpId && !isRfpBannerVisible && document) {
    document.cookie = `${CSN_SEARCH_BANNER_COOKIE_NAME}=${JSON.stringify({
      isLoginBannerDismissed: false,
      dismissedRfpBannerId: rfpId
    })}`;
  }

  // avoid scroll when search is expanded
  useEffect(() => {
    if (isSearchExpanded) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [isSearchExpanded]);

  const setIsSearchExpanded = async value => {
    const expandedSearch = document?.getElementById('expanded-search-container');
    if (!value) {
      expandedSearch?.classList.remove('animate-search-expand-open');
      expandedSearch?.classList.add('animate-search-expand-close');
      await new Promise(resolve => {
        // delay for exit animation to complete
        setTimeout(resolve, 300);
      });
    } else if (value) {
      expandedSearch?.classList.remove('animate-search-expand-close');
      expandedSearch?.classList.add('animate-search-expand-open');
    }
    setSearchExpanded(value);
  };

  const onAttendeesChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>, applyFilter = false) => {
      const { value } = e.target;
      // max allowed input must be one digit more than MAX_ATTENDEES_LIMIT.
      const stopInputAfterThisLimit = MAX_ATTENDEES_LIMIT * 10 + 9;
      if (parseInt(value, 10) > parseInt(stopInputAfterThisLimit.toString(), 10)) {
        return;
      }
      const updatedFilter = {
        ...searchBarFilters,
        LARGEST_ROOM: {
          ...searchBarFilters.LARGEST_ROOM,
          // ROOM_LAYOUT.BANQUET_60 has this factor <- default room layout
          minimum: Math.round(calculateRoomSpaceValue(Number(value), measurementUnit, 0.0832, false)[measurementUnit])
        },
        NUMBER_OF_ATTENDEES: Number(value)
      };
      setSearchBarFilters(updatedFilter);
      if (applyFilter) {
        await applyFilters(null, updatedFilter, true, undefined, undefined, false);
      }
    },
    [searchBarFilters, measurementUnit, applyFilters]
  );

  const onRoomsChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (e: any, isSetFromPopper = false, applyFilter = false) => {
      let value: string;
      if (e && e.target) {
        value = e.target.value;
      } else {
        value = e;
      }

      if (parseInt(value, 10) > parseInt(MAX_GUEST_ROOMS_LIMIT.toString(), 10)) {
        return;
      }

      let updatedFilter: Filter;
      if (Number(value) === 0) {
        updatedFilter = {
          ...searchBarFilters,
          NUMBER_OF_SLEEPING_ROOMS: {
            ...searchBarFilters.NUMBER_OF_SLEEPING_ROOMS,
            minimum: Number(value)
          },
          GUEST_CHECKIN_DATE: undefined,
          GUEST_CHECKOUT_DATE: undefined
        };
        setCheckInDate(undefined);
        setCheckOutDate(undefined);
      } else {
        updatedFilter = {
          ...searchBarFilters,
          NUMBER_OF_SLEEPING_ROOMS: {
            ...searchBarFilters.NUMBER_OF_SLEEPING_ROOMS,
            minimum: Number(value)
          },
          ...(isSetFromPopper && {
            GUEST_CHECKIN_DATE: checkInDate,
            GUEST_CHECKOUT_DATE: checkOutDate
          })
        };
      }

      setSearchBarFilters(updatedFilter);
      if (applyFilter) {
        await applyFilters(null, updatedFilter, true, undefined, undefined, false);
      }
    },
    [applyFilters, checkInDate, checkOutDate, searchBarFilters]
  );

  const handleRangeSelect = useCallback(
    (range: DateRange) => {
      setSearchBarFilters({ ...searchBarFilters, EVENT_START_DATE: range?.from, EVENT_END_DATE: range?.to });

      // format the date before showing in input
      const from = range?.from ? formatDate(locale, range?.from) : '';
      const to = range?.to ? formatDate(locale, range?.to) : '';
      setEventStart(from);
      setEventEnd(to);
    },
    [searchBarFilters, locale]
  );

  useEffect(() => {
    setAIMode(isAISearchEnabled && searchParams.get(queryStringConstants.AI) === 'true');
  }, [isAISearchEnabled, searchParams]);

  useEffect(() => {
    setSearchBarFilters(appliedFilters);

    if (appliedFilters.EVENT_START_DATE) {
      setEventStart(formatDate(locale, appliedFilters.EVENT_START_DATE));
    } else {
      setEventStart(undefined);
    }
    if (appliedFilters.EVENT_END_DATE) {
      setEventEnd(formatDate(locale, appliedFilters.EVENT_END_DATE));
    } else {
      setEventEnd(undefined);
    }
  }, [appliedFilters, locale, isSearchExpanded]);

  const filtersAndSort = (
    <div className="flex items-center justify-between lg:w-full">
      <FilterContainer
        setIsSearchExpanded={setIsSearchExpanded}
        isSearchExpanded={isSearchExpanded}
        aiMode={aiMode}
        searchTerm={searchTerm}
        geo={geo}
        locale={locale}
        measurementUnit={measurementUnit}
        appliedFilters={appliedFilters}
        applicableFilters={applicableFilters}
        options={options}
        isFiltersPanelOpen={isFiltersPanelOpen}
        setIsFiltersPanelOpen={setIsFiltersPanelOpen}
        locationRadiusMessage={locationRadiusMessage}
        isLoggedIn={isLoggedIn}
        sortOptions={sortOptions}
        sortType={sortType}
        setSortingType={setSortingType}
        setSortingLabel={setSortingLabel}
        sort={sort}
        setSort={setSort}
        featureConfig={featureConfig}
        setIsRfpBannerVisible={setIsRfpBannerVisible}
      />
      {view !== VIEW.FULL_MAP && !aiMode && (
        <div className="hidden items-center gap-2 lg:flex">
          <SortBy
            isSearchExpanded={isSearchExpanded}
            setIsSearchExpanded={setIsSearchExpanded}
            sortByLabel={options.localizedLabels.sortByLabel}
            setSortingType={setSort}
            setSortingLabel={setSortingLabel}
            sortOptions={sortOptions}
            sortingLabel={sortingLabel}
          />
        </div>
      )}
    </div>
  );

  const searchComponent = (
    <div className="flex w-full items-center lg:flex-col lg:gap-4">
      {/* search bar */}
      <SearchBarContainer
        inputRefs={inputRefs}
        setIsSearchExpanded={setIsSearchExpanded}
        onAttendeesChange={onAttendeesChange}
        onRoomsChange={onRoomsChange}
        handleRangeSelect={handleRangeSelect}
        eventStart={eventStart}
        eventEnd={eventEnd}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        setCheckInDate={setCheckInDate}
        setCheckOutDate={setCheckOutDate}
        searchTerm={searchTerm}
        filters={searchBarFilters}
        setFilters={setSearchBarFilters}
        labels={options.localizedLabels}
        countryCode={countryCode}
        locale={locale}
        lat={queryParserResult.geographicPointFilter?.geographicPoint.lat}
        lon={queryParserResult.geographicPointFilter?.geographicPoint.lon}
        radius={radius}
        cookieDomain={cookieDomain}
        isDatePickerOpen={isDatePickerOpen}
        setIsDatePickerOpen={setIsDatePickerOpen}
        setIsAutocompleteOpen={setIsAutocompleteOpen}
        isGuestRoomDatePickerOpen={isGuestRoomDatePickerOpen}
        setIsGuestRoomDatePickerOpen={setIsGuestRoomDatePickerOpen}
      />
      {/* filters */}
      {!isAISearchEnabled || (isAISearchEnabled && isSearchExpanded) ? <>{filtersAndSort}</> : <></>}
    </div>
  );

  const collapsedSearchWrapper = (
    // desktop - collapsed search bar with filter
    <>
      <div className="hidden w-full lg:block">
        <CollapsedSearch
          strings={options.localizedLabels}
          setIsSearchExpanded={setIsSearchExpanded}
          inputRefs={inputRefs}
          searchTerm={searchTerm}
          guestRooms={searchBarFilters.NUMBER_OF_SLEEPING_ROOMS?.minimum}
          attendees={searchBarFilters.NUMBER_OF_ATTENDEES}
          eventStart={eventStart}
          eventEnd={eventEnd}
          searchToggle={
            isAISearchEnabled ? (
              <SearchToggle aiMode={aiMode} strings={options.aiStrings} filters={searchBarFilters} />
            ) : (
              <></>
            )
          }
        >
          {/* desktop mode - filters */}
          {!isMobileOrTablet && <div className="-ml-1 flex lg:w-full">{filtersAndSort}</div>}
        </CollapsedSearch>
      </div>

      {/* mobile search bar */}
      {isMobileOrTablet && <div className="block w-full lg:hidden">{searchComponent}</div>}
    </>
  );

  const filtersAndSearch = !isSearchExpanded ? (
    collapsedSearchWrapper
  ) : (
    <ExpandedSearchContainer
      trapFocus={!(isDatePickerOpen || isAutocompleteOpen || isGuestRoomDatePickerOpen)}
      className={styles}
      setIsSearchExpanded={setIsSearchExpanded}
    >
      {searchComponent}
    </ExpandedSearchContainer>
  );

  // eslint-disable-next-line no-nested-ternary
  const filtersAndSearchForAI = !aiMode ? (
    !isSearchExpanded ? (
      <>
        <div className="flex w-full items-center">
          {/* filters - mobile and tablet mode  */}
          {isMobileOrTablet && <div className="flex">{filtersAndSort}</div>}
          {collapsedSearchWrapper}
          {
            <div className="flex lg:hidden">
              {/* AI toggle - mobile */}
              <SearchToggle aiMode={aiMode} strings={options.aiStrings} filters={searchBarFilters} />
            </div>
          }
        </div>
      </>
    ) : (
      <ExpandedSearchContainer
        trapFocus={!(isDatePickerOpen || isAutocompleteOpen || isGuestRoomDatePickerOpen)}
        className={styles}
        setIsSearchExpanded={setIsSearchExpanded}
      >
        {searchComponent}
      </ExpandedSearchContainer>
    )
  ) : (
    <AISearchContainer
      strings={options.aiStrings}
      searchTerm={searchTerm}
      isLoggedIn={isLoggedIn}
      searchToggle={<SearchToggle aiMode={aiMode} strings={options.aiStrings} filters={searchBarFilters} />}
      filter={appliedFilters}
      locale={locale}
      basePath={basePath}
    >
      {filtersAndSort}
    </AISearchContainer>
  );
  return (
    <div className={styles} data-cvent-id="filters-search-container" id="filters-search-container">
      {!isAISearchEnabled ? filtersAndSearch : filtersAndSearchForAI}
    </div>
  );
}
